import type {
  EnvironmentKind,
  EnvironmentListQuery,
  EnvironmentMetrics,
  EnvironmentSummary,
  LogShippingState,
  ShipLevel,
  Topology,
} from '@central/api';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { keys, useApi } from '../api';
import { useIsReseller } from './me';
import type { StatusTone } from '@central/ui';
import type { Point } from '../components/LineChart24h';

export const logShippingChip: Record<LogShippingState, { tone: StatusTone; label: string }> = {
  receiving: { tone: 'ok', label: 'Receiving' },
  waiting: { tone: 'unk', label: 'Waiting for first push' },
  stopped: { tone: 'warn', label: 'Stopped' },
  off: { tone: 'unk', label: 'Off' },
};

/** The lowest level a site ships, as the console names it. */
export const shipLevelLabel: Record<ShipLevel, string> = {
  debug: 'Debug and up',
  info: 'Info and up',
  warn: 'Warn and up',
  error: 'Error and up',
};

export { formatCpuMilli } from '@central/ui';

/** The hourly averages of one series, oldest first, for LineChart24h. */
export function hourlyPoints(
  m: EnvironmentMetrics | undefined,
  series: 'cpuPercent' | 'memUsedBytes',
): Point[] {
  return (m?.hours ?? []).map((h) => ({ at: h.hour, value: h[series].avg }));
}

export const topologyLabel: Record<Topology, string> = {
  standalone: 'Standalone',
  kubernetes: 'Kubernetes',
  compose: 'Compose',
};

export const kindLabel: Record<EnvironmentKind, string> = {
  dedicated: 'Dedicated',
  shared: 'Shared',
};

/**
 * A license key shortened for a meta line: its first and last five
 * characters around "…" ("ATMS-…-PL3D"); a key of 12 characters or fewer is
 * shown whole.
 */
export function shortKey(key: string): string {
  return key.length <= 12 ? key : `${key.slice(0, 5)}…${key.slice(-5)}`;
}

/** Dotted versions newest first, numeric parts compared as numbers. */
export function newestFirst(versions: readonly string[]): string[] {
  return [...versions].sort((a, b) => b.localeCompare(a, 'en', { numeric: true }));
}

/** The environment's sections, in design order; Health is the default. */
export const environmentTabs = {
  health: 'Health',
  metrics: 'Metrics',
  logs: 'Logs',
  versions: 'Versions',
  incidents: 'Incidents',
  enrollment: 'Enrollment',
} as const;

export type EnvironmentTab = keyof typeof environmentTabs;

/**
 * The pages of one filtered environment list, fetched as "Load more" asks;
 * a refetch reloads every page loaded so far.
 */
export function useEnvironmentPages(
  q: Omit<EnvironmentListQuery, 'cursor'>,
  opts: { refetchInterval?: number } = {},
) {
  const api = useApi();
  const query = useInfiniteQuery({
    ...opts,
    queryKey: keys.environmentList(q),
    queryFn: ({ pageParam }) => api.environments.list({ ...q, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  return { ...query, items: query.data?.pages.flatMap((p) => p.items) ?? [] };
}

/** Every environment of one customer, all pages; idle while customerId is ''. */
export function useCustomerEnvironments(customerId: string) {
  const api = useApi();
  const reseller = useIsReseller();
  return useQuery({
    queryKey: reseller
      ? keys.partnerEnvironmentOptions(customerId)
      : keys.environmentOptions(customerId),
    queryFn: async (): Promise<Array<Pick<EnvironmentSummary, 'id' | 'name' | 'kind'>>> => {
      if (reseller) return (await api.partner.environments.list({ customerId })).items;
      const items: EnvironmentSummary[] = [];
      let cursor: string | undefined;
      do {
        const page = await api.environments.list({ customerId, cursor, limit: 200 });
        items.push(...page.items);
        cursor = page.nextCursor;
      } while (cursor);
      return items;
    },
    enabled: !!customerId,
  });
}

/** Select options for environments; a shared one says so. */
export const environmentOptions = (
  envs: Array<Pick<EnvironmentSummary, 'id' | 'name' | 'kind'>> | undefined,
) =>
  (envs ?? []).map((e) => ({
    value: e.id,
    label: e.kind === 'shared' ? `${e.name} (shared)` : e.name,
  }));

/** The latest kept report's services, or with `at` the latest report at or before it. */
export function useServices(environmentId: string, at?: string) {
  const api = useApi();
  return useQuery({
    queryKey: at
      ? keys.environmentServicesAt(environmentId, at)
      : keys.environmentServices(environmentId),
    queryFn: () => api.environments.services(environmentId, at ? { at } : {}),
  });
}
